import { Big } from 'big.js'
import { isNil } from 'lodash'

import { isEmpty, pick } from './object'

export const RoundingMode = pick(Big, [
  'roundDown',
  'roundUp',
  'roundHalfEven',
  'roundHalfUp'
])

export const round = (
  value: number | string,
  options?: {
    precision?: number
    roundingMode?: Big.RoundingMode
  }
) => {
  return new Big(value).toFixed(options?.precision, options?.roundingMode)
}

const style = (
  value: string | number,
  options: Intl.NumberFormatOptions = {}
) => {
  const { minimumFractionDigits, ...restOptions } = options

  if (isNil(value) || isNaN(Number(value))) {
    return ''
  }

  const precision = minimumFractionDigits ?? `${value}`.split('.')[1]?.length

  return new Intl.NumberFormat('zh-Hans-CN', {
    minimumFractionDigits: precision,
    ...restOptions
  }).format(Number(value))
}

export const pretty = (
  value?: number | string,
  options?: {
    precision?: number
    currency?: boolean
  }
) => {
  return isEmpty(value)
    ? undefined
    : style(
        round(value, options),
        options?.currency
          ? {
              style: 'currency',
              currency: 'CNY'
            }
          : undefined
      )
}

export const toBig = (value: number | string) => new Big(value)

/**
 * 判断是否是小数
 */
const DECIMAL_REGEXP = /^-?\d+(\.\d+)?$/
export const isDecimal = (val?: string) => {
  return isNil(val) ? false : DECIMAL_REGEXP.test(val)
}

/**
 * 判断是否是有限小数
 *
 * @param numerator 分子
 * @param denominator 分母
 */
export const isFiniteDecimal = (numerator: number, denominator: number) => {
  // 查找分子跟分母的公约数
  function findGCD(a: number, b: number) {
    if (b === 0) {
      return a
    }
    return findGCD(b, a % b)
  }

  const gcd = findGCD(numerator, denominator)
  // 约分之后的最小分母
  let reducedDenominator = denominator / gcd

  // 如果分母的质因数只包含2和5，那么就是有限小数
  while (reducedDenominator % 2 === 0) {
    reducedDenominator /= 2
  }
  while (reducedDenominator % 5 === 0) {
    reducedDenominator /= 5
  }

  return reducedDenominator === 1
}
